import React from 'react'
import { Button } from 'antd'
import { useIntl } from 'react-intl'

import { Banner, BannerProps } from '../../../../design-system/Banner'
import { ResetFailureReason, SigninBanner } from '../types'

/**
 * A banner's ask, under the text it answers. The shared Banner hands an action to Ant's
 * trailing slot, which sits level with the first line and beside the close button. There it reads
 * as part of dismissing the banner, and drifts away from the words as soon as they wrap. The
 * placement is done here rather than in a component every page shares.
 */
const withAction = (text: React.ReactNode, label: string, onClick: () => void) => (
  <>
    {text}
    <div className="signin-banner-action">
      <Button type="link" size="small" onClick={onClick}>
        {label}
      </Button>
    </div>
  </>
)

/** The copy the reset modal used for each Cognito failure, kept as it was. */
const RESET_FAILURE_COPY: Record<ResetFailureReason, { titleId: string; descriptionId: string }> = {
  codeMismatch: { titleId: 'error.codeMismatchException', descriptionId: 'error.codeMismatchExceptionMessage' },
  codeExpired: { titleId: 'error.expiredCodeException', descriptionId: 'error.expiredCodeExceptionMessage' },
  limitExceeded: {
    titleId: 'error.passwordResetLimitExceededException',
    descriptionId: 'error.passwordResetLimitExceededExceptionMessage',
  },
  invalidPassword: { titleId: 'error.invalidPasswordException', descriptionId: 'error.invalidPasswordExceptionMessage' },
  deliveryFailed: {
    titleId: 'error.codeDeliveryFailureException',
    descriptionId: 'error.codeDeliveryFailureExceptionMessage',
  },
  unknown: { titleId: 'error.generic', descriptionId: 'signin.resetFailedDescription' },
}

export interface ISigninBannerProps {
  banner: SigninBanner
  onSignUp: () => void
  onForgotPassword: () => void
  onDismiss: () => void
  /** Takes the address the banner is about rather than the one the page last handled. */
  onRequestAccess: (email: string) => void
  /** The no-access ask, which goes with the provider sign-in rather than the address. */
  onRequestPlatformAccess: () => void
  onContactSupport: () => void
  /** A request is on its way, so the ask is held until it answers. */
  isRequestingAccess?: boolean
}

const SigninBannerView = ({
  banner,
  onSignUp,
  onForgotPassword,
  onDismiss,
  onRequestAccess,
  onRequestPlatformAccess,
  onContactSupport,
  isRequestingAccess = false,
}: ISigninBannerProps): React.ReactElement | null => {
  const { formatMessage, formatDate } = useIntl()

  if (banner.kind === 'none') return null

  // The request-access ask, under the text like every other one, but held while a request is on
  // its way: each click notifies the company's admins, so a double click must not send it twice.
  const withRequestAccess = (text: React.ReactNode, onClick: () => void) => (
    <>
      {text}
      <div className="signin-banner-action">
        <Button type="link" size="small" onClick={onClick} disabled={isRequestingAccess} loading={isRequestingAccess} data-qa="signin-request-access">
          {formatMessage({ id: 'signin.requestAccess' })}
        </Button>
      </div>
    </>
  )

  // Every banner can be closed, whether or not it clears itself, so the button is added in
  // one place rather than repeated on each of them.
  const wrap = (content: React.ReactElement<BannerProps>) => (
    <div className="signin-banner" data-qa="signin-banner" data-banner-kind={banner.kind}>
      {React.cloneElement(content, {
        closable: { 'aria-label': formatMessage({ id: 'app.close' }) },
        onClose: onDismiss,
      })}
    </div>
  )

  switch (banner.kind) {
    case 'noAccount':
      return wrap(<Banner tone="info" message={withAction(formatMessage({ id: 'signin.noAccountFound' }), formatMessage({ id: 'signin.noAccountFound.signUp' }), onSignUp)} />)

    // Said of the provider account, not the address: the lookup behind it was by workspace or
    // tenant, and the same address can still have an account somewhere else. Support is the
    // way out for someone who expected their company to be here.
    case 'noPlatformAccount':
      return wrap(
        <Banner
          tone="info"
          message={
            <>
              {formatMessage({ id: 'signin.noPlatformAccountFound' }, { platform: banner.platform })}
              <div className="signin-banner-action">
                <Button type="link" size="small" onClick={onSignUp} data-qa="signin-no-platform-account-signup">
                  {formatMessage({ id: 'signin.noAccountFound.signUp' })}
                </Button>
                {' · '}
                <Button type="link" size="small" onClick={onContactSupport} data-qa="signin-no-platform-account-support">
                  {formatMessage({ id: 'app.contactSupport' })}
                </Button>
              </div>
            </>
          }
        />
      )

    // Before the button, not after the wrong account has come back: the page cannot see who is
    // signed in to the provider, so it goes by the last sign-in in this browser and says "may".
    case 'previousPlatformAccount':
      return wrap(
        <Banner
          tone="warning"
          message={formatMessage({ id: 'signin.platformStep.previousAccountTitle' }, { platform: banner.platform, previousEmail: banner.previousEmail })}
          description={formatMessage(
            { id: 'signin.platformStep.previousAccountDescription' },
            { platform: banner.platform, previousEmail: banner.previousEmail, email: banner.email }
          )}
        />
      )

    // The step under it is that platform's sign-in, button included, so this only says why the
    // page moved there.
    case 'orgOnOtherPlatform':
      return wrap(<Banner tone="info" message={formatMessage({ id: 'connect.youAreAlreadyMember' }, { existingOrganizationName: banner.organizationName })} />)

    // Only an administrator can undo this, so the banner carries the way to reach them.
    case 'inactiveAccount':
      return wrap(<Banner tone="warning" message={withRequestAccess(formatMessage({ id: 'signin.inactiveAccount' }), () => onRequestAccess(banner.email))} />)

    // Sign-up sent the visitor here with an account that is deactivated: the company is on
    // Vacation Tracker, so there is nothing to sign up for, and only an administrator can
    // give the access back.
    case 'accessNeeded':
      return wrap(
        <Banner
          tone="warning"
          message={formatMessage({ id: 'components.companyAlreadySignup.title' })}
          description={withRequestAccess(formatMessage({ id: 'signin.accessNeeded.description' }), () => onRequestAccess(banner.email))}
        />
      )

    // A platform sign-in found the company but not this address in it. Only an administrator
    // can add it, so the ask sits under the reason, as on the deactivated-account banners. It
    // carries the sign-in, not the address: the address alone cannot say which company.
    case 'noAccess':
      return wrap(
        <Banner
          tone="warning"
          message={formatMessage({ id: 'signin.noAccess.title' })}
          description={withRequestAccess(
            banner.organizationName
              ? formatMessage({ id: 'signin.noAccess.descriptionNamed' }, { organizationName: banner.organizationName })
              : formatMessage({ id: 'signin.noAccess.description' }),
            onRequestPlatformAccess
          )}
        />
      )

    // Says "if an account exists": the endpoint answers the same way for an
    // address it did nothing for, and so does this.
    case 'accessRequested':
      return wrap(<Banner tone="success" message={formatMessage({ id: 'signin.requestAccessSent' })} />)

    // Asking again before this date reaches nobody, so the page says when it is worth doing.
    case 'accessAlreadyRequested':
      return wrap(<Banner tone="info" message={formatMessage({ id: 'signin.requestAccessAlreadySent' }, { date: formatDate(new Date(banner.retryOn), { dateStyle: 'long' }) })} />)

    case 'accessRequestFailed':
      return wrap(<Banner tone="danger" live="assertive" message={formatMessage({ id: 'signin.requestAccessFailed' })} />)

    case 'invalidEmail':
      return wrap(<Banner tone="warning" message={formatMessage({ id: 'signin.invalidEmail' })} />)

    // No action: the form already carries a "Forgot your password?" link a few pixels away,
    // and a wrong password needs correcting, not explaining.
    case 'incorrectPassword':
      return wrap(<Banner tone="danger" live="assertive" message={formatMessage({ id: 'signin.passwordDoesNotMatch' })} />)

    // Same shape as a wrong password: the boxes to correct it are a few pixels below, so the
    // line says what happened and gets out of the way.
    case 'mfaCodeMismatch':
      return wrap(<Banner tone="danger" live="assertive" message={formatMessage({ id: 'signin.mfaCodeMismatch' })} />)

    // Not the code but the attempt: an expired challenge or a request that never landed.
    case 'mfaFailed':
      return wrap(<Banner tone="danger" live="assertive" message={formatMessage({ id: 'signin.mfaVerificationFailed' })} />)

    // Only said once the tab is cut off, when this page cannot know whether it is open, so it
    // says what holds either way rather than calling the attempt cancelled.
    case 'continueInPlatformTab':
      return wrap(
        <Banner
          tone="info"
          message={formatMessage({ id: 'signin.continueInPlatformTab.title' }, { platform: banner.platform })}
          description={formatMessage({ id: 'signin.continueInPlatformTab.description' }, { platform: banner.platform })}
        />
      )

    // The three below replaced corner toasts. A visitor coming back from a provider tab finds
    // the answer anchored to the page rather than gone from a corner they never looked at.
    case 'platformCancelled':
      return wrap(
        <Banner
          tone="warning"
          message={formatMessage({ id: 'connect.signinCancelledTitle' })}
          description={formatMessage({ id: 'connect.signinCancelledDescription' }, { platform: banner.platform })}
        />
      )

    // Cognito reports every rejected provider token the same way, so this says what is true of
    // all of them rather than guessing which one it was.
    case 'platformSigninRejected':
      return wrap(
        <Banner
          tone="danger"
          live="assertive"
          message={formatMessage({ id: 'signin.platformSigninRejected' }, { platform: banner.platform })}
          description={formatMessage({ id: 'signin.platformSigninRejectedDescription' })}
        />
      )

    case 'subscriptionExpired':
      return wrap(<Banner tone="warning" message={formatMessage({ id: 'connect.subscriptionExpiredTitle' })} description={formatMessage({ id: 'error.subscriptionExpired' })} />)

    case 'notWorkspaceAccount':
      return wrap(
        <Banner
          tone="warning"
          message={formatMessage({ id: 'connect.google.notWorkspaceUserTitle' })}
          description={formatMessage({ id: 'connect.google.notWorkspaceUserDescription' })}
        />
      )

    case 'missingAttributes':
      return wrap(
        <Banner
          tone="danger"
          live="assertive"
          message={formatMessage({ id: 'error.forceChangePassword.missingAttributesTitle' })}
          description={formatMessage({ id: 'error.forceChangePassword.missingAttributesDescription' }, { attributes: banner.attributes })}
        />
      )

    // Sign-up sent the visitor here with an address that already has an account. The modal this
    // replaces ended in "simply click here:" above its own button; the buttons are outside the
    // banner now, and the step under it already says what to choose, so the line just explains
    // why sign-up moved them and points down.
    case 'companyAlreadySignedUp':
      return wrap(
        <Banner
          tone="info"
          message={formatMessage({ id: 'components.companyAlreadySignup.title' })}
          description={formatMessage({ id: 'signin.companyAlreadySignedUp.description' })}
        />
      )

    case 'codeResent':
      return wrap(<Banner tone="success" message={formatMessage({ id: 'notification.codeResent' })} description={formatMessage({ id: 'notification.codeResentMessage' })} />)

    // The reset does not sign anyone in, so this lands on the password step it came from.
    case 'passwordResetDone':
      return wrap(<Banner tone="success" message={formatMessage({ id: 'notification.passwordReset' })} description={formatMessage({ id: 'notification.passwordResetMessage' })} />)

    case 'resetFailed':
      return wrap(
        <Banner
          tone="danger"
          live="assertive"
          message={formatMessage({ id: RESET_FAILURE_COPY[banner.reason].titleId })}
          description={formatMessage({ id: RESET_FAILURE_COPY[banner.reason].descriptionId })}
        />
      )

    case 'passwordResetRequired':
      return wrap(
        <Banner
          tone="warning"
          message={formatMessage({ id: 'error.passwordResetRequired' })}
          description={withAction(formatMessage({ id: 'error.passwordResetRequiredMessage' }), formatMessage({ id: 'app.resetPassword' }), onForgotPassword)}
        />
      )

    default:
      return wrap(<Banner tone="danger" live="assertive" message={formatMessage({ id: 'signin.lookupFailed' })} />)
  }
}

export default SigninBannerView
